import { defineStore } from 'pinia'
import { RouteLocationRaw } from 'vue-router'

import { useMenuStore } from './menuStore'

interface IBreadcrumbItem {
  id?: string
  title: string
  disabled?: boolean
  href?: string
  to?: string | RouteLocationRaw
}

const DEFAULT_BREADCRUMB: IBreadcrumbItem = {
  id: '',
  title: 'Главная страница',
  disabled: false,
  href: '/'
}

export const useBreadcrumbsStore = defineStore('breadcrumbs', {
  state: () => ({
    items: [] as IBreadcrumbItem[]
  }),

  actions: {
    /**
     * Устанавливает новые хлебные крошки
     * @param {Array} newItems - Массив объектов { id, title, href, disabled }
     */
    setBreadcrumbs (newItems: IBreadcrumbItem[]) {
      this.items = newItems
    },

    /**
     * Добавляет хлебную крошку в конец
     * @param {Object} item - Объект { id, title, href, disabled }
     */
    addBreadcrumb (item: IBreadcrumbItem) {
      this.items.push(item)
    },

    /**
     * Удаляет последнюю хлебную крошку
     */
    popBreadcrumb () {
      this.items.pop()
    },

    /**
     * Очищает все хлебные крошки
     */
    clearBreadcrumbs () {
      this.items = []
    },

    /**
     * Заменяет последнюю хлебную крошку
     * @param {Object} item - Объект { id, title, href, disabled }
     */
    replaceLastBreadcrumb (item: IBreadcrumbItem) {
      if (this.items.length > 0) {
        this.items[this.items.length - 1] = item
      } else {
        this.addBreadcrumb(item)
      }
    },
  },

  getters: {
    /**
     * Возвращает текущие хлебные крошки с дефолтным элементом в начале
     */
    currentBreadcrumbs: (state) => {
      const processedItems = state.items.length > 0
        ? [...state.items]
        : []

      const menuStore = useMenuStore()

      if (menuStore.getActiveItem && menuStore.getActiveItem.title !== 'Главная') {
        const activeChild = menuStore.getActiveItem.children.find(item => item.active)
        if (activeChild) {
          processedItems.unshift({
            id: activeChild.id,
            title: activeChild.title,
            disabled: !activeChild.active,
            href: activeChild.url
          })
        }

        processedItems.unshift({
          id: menuStore.getActiveItem.id,
          title: menuStore.getActiveItem.title,
          disabled: !menuStore.getActiveItem.active,
          href: menuStore.getActiveItem.url
        })
      }

      processedItems.unshift(DEFAULT_BREADCRUMB)

      // Если есть элементы, последний должен быть disabled
      if (processedItems.length > 1) {
        const lastItem = processedItems[processedItems.length - 1]
        if (lastItem) processedItems[processedItems.length - 1] = {
          ...lastItem,
          disabled: true
        }
      }

      return processedItems
    },

    /**
     * Возвращает последнюю хлебную крошку
     */
    lastBreadcrumb: (state) => state.items.length > 0
      ? state.items[state.items.length - 1]
      : null,

    /**
     * Возвращает количество хлебных крошек
     */
    length: (state) => state.items.length
  }
})
